import { useCallback, useMemo, type FC } from "react";
import { useListServiceAccounts } from "@/queries/service-account-queries";
import { useAuthenticatedUser } from "@/queries/user-queries";
import { AutomationsCapabilities, useFeatureAutomations } from "@/hooks/use-feature-automations";
import { Principal, type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { Avatar, AvatarFallback, AvatarImage, AvatarInitials } from "@/components/Avatar";
import { ServiceAccountAvatar } from "@/components/settings/organization/service-accounts/ServiceAccountAvatar";
import { cn } from "@/components/podkit/lib/cn";
import type { PlainMessage } from "@bufbuild/protobuf";
import { Combobox } from "@/components/flexkit/combobox";

type ExecutorOption = {
    id: string;
    name: string;
    principal: Principal.SERVICE_ACCOUNT | Principal.USER;
    avatarUrl?: string;
};

type WorkflowEditExecutorProps = {
    selectedExecutorId?: string;
    onExecutorChange: (executor?: PlainMessage<Subject>) => void;
    disabled?: boolean;
    className?: string;
};

export const WorkflowEditExecutor: FC<WorkflowEditExecutorProps> = ({
    selectedExecutorId,
    onExecutorChange,
    disabled = false,
    className,
}) => {
    const { data: serviceAccountsData, isPending: isLoadingServiceAccounts } = useListServiceAccounts();
    const { data: currentUser, isPending: isLoadingUser } = useAuthenticatedUser();
    const { capabilities } = useFeatureAutomations();
    const canUseServiceAccountExecutors = capabilities.has(AutomationsCapabilities.ServiceAccountExecutor);

    const executorOptions = useMemo((): ExecutorOption[] => {
        const options: ExecutorOption[] = [];

        if (currentUser) {
            options.push({
                id: currentUser.id,
                name: currentUser.name,
                principal: Principal.USER,
                avatarUrl: currentUser.avatarUrl,
            });
        }

        if (canUseServiceAccountExecutors && serviceAccountsData?.serviceAccounts) {
            serviceAccountsData.serviceAccounts.forEach((sa) => {
                options.push({
                    id: sa.id,
                    name: sa.name,
                    principal: Principal.SERVICE_ACCOUNT,
                });
            });
        }

        return options;
    }, [currentUser, canUseServiceAccountExecutors, serviceAccountsData]);

    const selectedExecutor = useMemo(() => {
        return executorOptions.find((option) => option.id === selectedExecutorId);
    }, [executorOptions, selectedExecutorId]);

    const handleExecutorChange = useCallback(
        (executorId: string) => {
            const executor = executorOptions.find((option) => option.id === executorId);
            if (executor) {
                onExecutorChange({
                    id: executor.id,
                    principal: executor.principal,
                });
            }
        },
        [executorOptions, onExecutorChange],
    );

    const isLoading = isLoadingServiceAccounts || isLoadingUser;

    let comboboxContent: React.ReactNode;
    if (isLoading) {
        comboboxContent = <Combobox.Loading>Loading identities...</Combobox.Loading>;
    } else if (executorOptions.length > 0) {
        comboboxContent = (
            <Combobox.List
                items={executorOptions}
                searchKeys={["name"]}
                noMatchesComponent={<Combobox.Empty>No matching identities.</Combobox.Empty>}
            >
                {(option) => <ExecutorOption key={option.id} option={option} />}
            </Combobox.List>
        );
    } else {
        comboboxContent = <Combobox.Empty>No identities found.</Combobox.Empty>;
    }

    return (
        <div className={cn(className)}>
            <Combobox
                value={selectedExecutorId || ""}
                onValueChange={handleExecutorChange}
                disabled={disabled}
                loading={isLoading}
                className="w-full"
            >
                <Combobox.Value>
                    {selectedExecutor ? (
                        <div className="flex items-center gap-2">
                            <ExecutorAvatar option={selectedExecutor} />
                            <Combobox.ValueLabel>{selectedExecutor.name}</Combobox.ValueLabel>
                        </div>
                    ) : (
                        "Select identity"
                    )}
                </Combobox.Value>
                {comboboxContent}
            </Combobox>
        </div>
    );
};

const ExecutorAvatar: FC<{ option: ExecutorOption }> = ({ option: { id, principal, avatarUrl, name } }) => {
    if (principal === Principal.USER) {
        return (
            <Avatar size={24}>
                {avatarUrl && <AvatarImage src={avatarUrl} alt={`${name}'s avatar`} />}
                <AvatarFallback>
                    <AvatarInitials name={name} size={24} />
                </AvatarFallback>
            </Avatar>
        );
    }
    return <ServiceAccountAvatar id={id} size={24} />;
};

const ExecutorOption: FC<{ option: ExecutorOption }> = ({ option }) => {
    const { name } = option;
    return (
        <Combobox.ListItem value={option.id}>
            <Combobox.ListItemLeadingIcon>
                <ExecutorAvatar option={option} />
            </Combobox.ListItemLeadingIcon>
            <Combobox.ListItemTitle className="text-base">{name}</Combobox.ListItemTitle>
        </Combobox.ListItem>
    );
};
